import { getEnv } from "@ace/env";
import { useEffect, useState } from "react";
import { COUNTRY_RESTRICTION_PAGE } from "../../constants";
import { registerModalAsSeenByUser } from "../../services/register-modal-as-seen-by-user";
import { getRegistrationStatusOfModalForUser } from "../../services/get-registration-status-of-modal-for-user";

export interface CountryType {
  id: number;
  countryCode: string;
  countryName: string;
  riskScenarioId: number;
  created: string;
  updated: string;
}

interface UseCountriesType {
  countries: CountryType[];
  isBlacklisted: (country: string) => boolean;
  loading: boolean;
  getCountry: (country: string) => CountryType | null;
  isCountryRestrictionsAccepted: (token: string) => Promise<boolean>;
  acceptCountryRestrictions: (token: string) => Promise<void>;
}

export const useCountries: () => UseCountriesType = () => {
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getCountries = async () => {
      setLoading(true);
      const response = await fetch(
        `${getEnv("VITE_ACE_WEB_AUTH_SERVER")}/countries`,
        {
          method: "GET",
        },
      );
      const json = await response.json();

      if (json.status === "ERROR") {
        setLoading(false);
        throw new Error(json.error[0].msg);
      }

      setLoading(false);
      setCountries(json.data);
    };

    getCountries();
  }, []);

  const isBlacklisted = (countryName: string): boolean => {
    return (
      countries.find((country) => country.countryName === countryName)
        ?.riskScenarioId === 8
    );
  };

  const getCountry = (countryName: string): CountryType | null => {
    return (
      countries.find((country) => country.countryName === countryName) || null
    );
  };

  const isCountryRestrictionsAccepted = async (token: string) => {
    let isAccepted = false;
    const response = await getRegistrationStatusOfModalForUser(
      token,
      COUNTRY_RESTRICTION_PAGE,
    );
    isAccepted = !!response.shownOn;
    return isAccepted;
  };

  const acceptCountryRestrictions = async (token: string) => {
    await registerModalAsSeenByUser(token, COUNTRY_RESTRICTION_PAGE);
  };

  return {
    countries,
    isBlacklisted,
    loading,
    getCountry,
    isCountryRestrictionsAccepted,
    acceptCountryRestrictions,
  };
};
